import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToMDY,
  DateSelect,
  ChartOfAccountsSelect,
  FormatDateToWord
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

export default function GeneralLedgerReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Variables 
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  
  const [glAccount, setGLAccount] = useState(null);
  const [showGLAccountSelectForm, setShowGLAccountSelectForm] = useState(false);
  const openGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(true);
  }
  const closeGLAccountSelectForm = () => {
    setShowGLAccountSelectForm(false);
  }
//#endregion






//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    const body = {
      glAccountId: glAccount?.id,
      dateFrom: dateFrom,
      dateTo: dateTo,
    }

    try {
      // const response = await axios.post(`${apiUrl}/api/Report/report-data/statement-of-account`, body, {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/general-ledger-report`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [glAccount, dateFrom, dateTo]);

  const generateHandleClick = async () => {
    if (!dateFrom || !dateTo || !company) {
        setFailMessage("Please fill up required fields");
        return;
    }

    setLoading(true);
    const _data = await fetchReportData();
    console.log({_data})

    if (!_data || _data?.length <= 0) {
        setFailMessage("No record found");
        setLoading(false);
        return;
    }

    try {
        const pdfUrl = `${window.location.origin}/docs/GL_ACCOUNT_REPORT.pdf`;
        const templateBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
        const templateDoc = await PDFDocument.load(templateBytes);
        
        const response = await fetch(pdfUrl);
        const pdfBytes = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(pdfBytes);

        pdfDoc.registerFontkit(fontkit);
        const fontResponse = await fetch("/calibri.ttf");
        const fontBytes = await fontResponse.arrayBuffer();
        const calibriFont = await pdfDoc.embedFont(fontBytes);

        const boldResponse = await fetch("/calibrib.ttf");
        const boldBytes = await boldResponse.arrayBuffer();
        const calibriBold = await pdfDoc.embedFont(boldBytes);

        const wrapText = (text, maxWidth, font, fontSize) => {
          if (!text) return [""];
          
          const words = text.split(" ");
          const lines = [];
          let currentLine = "";

          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;

            if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
              currentLine = testLine;
            } else {
              // If the word itself exceeds maxWidth, force character-by-character wrapping
              if (font.widthOfTextAtSize(word, fontSize) > maxWidth) {
                if (currentLine) lines.push(currentLine);
                currentLine = "";

                let chunk = "";
                for (const char of word) {
                  if (font.widthOfTextAtSize(chunk + char, fontSize) <= maxWidth) {
                    chunk += char;
                  } else {
                    lines.push(chunk);
                    chunk = char;
                  }
                }
                currentLine = chunk;
              } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              }
            }
          }

          if (currentLine) lines.push(currentLine);
          return lines;
        };

        const drawHeader = (targetPage) => {
          targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 571, size: 16, font: calibriBold });
          targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 561, size: 8, font: calibriFont });
          targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 551, size: 8, font: calibriFont });
          
          targetPage.drawText(`${FormatDateToWord(dateFrom)} to ${FormatDateToWord(dateTo)}`, { x: 85, y: 526, size: 8, font: calibriBold });
        };

        let currentPage = pdfDoc.getPages()[0];
        drawHeader(currentPage);

        let yPosition = 456;
        const bottomThreshold = 80;
        const lineHeight = 12;

        const colWidthDocNo = 40;
        const colWidthAccountName = 80;
        const colWidthRefNo = 80;
        const colWidthCompany = 90;
        const colWidthRemarks = 90;

        const addNewPage = async () => {
          const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
          currentPage = pdfDoc.addPage(templatePage);
          drawHeader(currentPage);
          yPosition = 456;
        };

        let totalDebit = 0;
        let totalCredit = 0;
        let totalAmount = 0;

        for (const record of _data) {
          if (yPosition < bottomThreshold) {
            await addNewPage();
          }

          let trn = "REC"

          if(record.vendorId){
            trn = "PAY"
          }
          
          currentPage.drawText(trn, { x: 40, y: yPosition, size: 8, font: calibriFont });

          const docNoLines = wrapText(record.referenceNumber ?? "", colWidthDocNo, calibriFont, 8);
          const accountNameLines = wrapText(record.glAccountName ?? "", colWidthAccountName, calibriFont, 8);
          const refNoLines = wrapText(record.referenceNumber ?? "", colWidthRefNo, calibriFont, 8);
          const companyLines = wrapText(record.company?.companyName1 ?? "", colWidthCompany, calibriFont, 8);
          const remarksLines = wrapText(record.description ?? "", colWidthRemarks, calibriFont, 8);

          const maxLines = Math.max(docNoLines.length, accountNameLines.length, refNoLines.length, companyLines.length, remarksLines.length, 1);
          const rowHeight = maxLines * lineHeight;

          // --- PAGE BREAK CHECK FOR ITEM ROW ---
          if (yPosition - rowHeight < bottomThreshold) {
            await addNewPage();
          }

          // Draw wrapped description
          for (let i = 0; i < docNoLines.length; i++) {
            currentPage.drawText(docNoLines[i], {
              x: 65,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          currentPage.drawText(`${record.glAccountNumber ?? ""}`, { x: 110, y: yPosition, size: 8, font: calibriFont });

          // Draw wrapped description
          for (let i = 0; i < accountNameLines.length; i++) {
            currentPage.drawText(accountNameLines[i], {
              x: 153,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          // Draw wrapped description
          for (let i = 0; i < refNoLines.length; i++) {
            currentPage.drawText(refNoLines[i], {
              x: 265,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          currentPage.drawText(`${FormatDateToMDY(record.transactionDate ?? "")}`, { x: 340, y: yPosition, size: 8, font: calibriFont });

          const date = record.transactionDate
            ? new Date(record.transactionDate)
            : null;

          const month = date ? date.getMonth() + 1 : "";
          const year = date ? date.getFullYear() : "";

          currentPage.drawText(`${month}`, { x: 390, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${year}`, { x: 420, y: yPosition, size: 8, font: calibriFont });

          var debit = record.debitCredit === "Debit" ? record.amount : 0;
          var credit = record.debitCredit === "Credit" ? record.amount : 0;

          totalDebit += debit;
          totalCredit += credit;
          totalAmount += record.amount;

          currentPage.drawText(`${FormatValueToDecimal(debit)}`, { x: 453, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(credit)}`, { x: 502, y: yPosition, size: 8, font: calibriFont });
          currentPage.drawText(`${FormatValueToDecimal(record.amount)}`, { x: 550, y: yPosition, size: 8, font: calibriFont });

          
          // Draw wrapped description
          for (let i = 0; i < companyLines.length; i++) {
            currentPage.drawText(companyLines[i], {
              x: 605,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }

          
          // Draw wrapped description
          for (let i = 0; i < remarksLines.length; i++) {
            currentPage.drawText(remarksLines[i], {
              x: 690,
              y: yPosition - i * lineHeight,
              size: 8,
              font: calibriFont,
            });
          }
          
          yPosition -= rowHeight + 5;
        }

        if (yPosition < 100) {
          await addNewPage();
        }
  
        currentPage.drawLine({ start: { x: 35, y: yPosition }, end: { x: 757, y: yPosition }, thickness: 1 });
        yPosition -= 15;
        currentPage.drawText(`${FormatValueToDecimal(totalDebit)}`, { x: 448, y: yPosition, size: 8, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(totalCredit)}`, { x: 502, y: yPosition, size: 8, font: calibriBold });
        currentPage.drawText(`${FormatValueToDecimal(totalAmount)}`, { x: 550, y: yPosition, size: 8, font: calibriBold });

        const modifiedPdfBytes = await pdfDoc.save();
        const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `GL_ACCOUNT_REPORT.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } catch (error) {
        console.error("Error:", error);
        setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasGeneralLedgerReport) {
    return <NoAccess />;
  }
//#endregion






//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`General Ledger Report`}
            </h2>
            <p className="text-gray-500 mt-1">Complete activity and balance of account.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Account
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Account"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={glAccount?.accountName || ""}
                readOnly
              />
              {!glAccount ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openGLAccountSelectForm}>
                  <ManageSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setGLAccount(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>
      
      <ChartOfAccountsSelect 
        visible={showGLAccountSelectForm} 
        onClose={closeGLAccountSelectForm}
        modalName={"Select Account"}
        data={glAccount} setData={setGLAccount}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}